import { useState, useEffect, useRef } from 'react';
import { NODE_W, NODE_H, NODE_W_ROOT, NODE_H_ROOT, NODE_RX } from '../constants';
import { getNodeColors } from '../utils/colors';
import { useT } from '../i18n';

const FLASHCARD_W = 228;
const FLASHCARD_H = 140;

const maskTitleTerms = (text = '', title = '') => {
  if (!text || !title) return text;
  let masked = text;
  const words = title.split(/[\s,.-]+/).map(w => w.trim()).filter(w => w.length > 2);
  words.forEach((word) => {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    masked = masked.replace(new RegExp(escaped, 'gi'), '____');
  });
  return masked;
};

export const NodeItem = ({
  node,
  isSelected,
  isMultiSelected,
  isHovered,
  connectState,
  startConnect,
  onMouseDown,
  onNodeClick,
  onHover,
  onEditNode,
  onAddNode,
  flashcardMode,
  showFlashcardIcons,
  isRevealed,
  onOpenStudyCard,
  isMobile = false,
  isAILoading = false,
  supportsPointer = false,
}) => {
  const t = useT();
  const isProblem = node.type === 'problem';
  const { fillColor, strokeColor, textColor, outlineColor, outlineWidth, shadowColor } = getNodeColors(node);
  const isStudyCard = flashcardMode && node.isFlashcard;
  const cardStatus = node.flashcardData?.status || 'new';
  const cardType = node.flashcardData?.type || 'cloze';
  const cardFront = (node.flashcardData?.front || node.summary || node.title || '').trim();
  const W = isStudyCard ? FLASHCARD_W : (isProblem ? NODE_W_ROOT : NODE_W);
  const H = isStudyCard ? FLASHCARD_H : (isProblem ? NODE_H_ROOT : NODE_H);
  const isFlashcardHidden = isStudyCard && !isRevealed;
  const isGhost = node._isGhost;

  const getHintText = (summary, title) => {
    if (!summary) return '?';

    let processed = summary;

    if (title && title.length > 2) {
      const words = title.split(/[\s,.-]+/).filter(w => w.length > 2);
      words.forEach(word => {
        const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordRegex = new RegExp(`(?<!{)${escapedWord}(?!})`, 'gi');
        processed = processed.replace(wordRegex, `{${word}}`);
      });
    }

    return processed.replace(/{[^}]+}/g, '...');
  };

  const [showRipple, setShowRipple] = useState(false);
  const lastBlueClickRef = useRef(0);

  useEffect(() => {
    if (!node._hidden) {
      const t1 = setTimeout(() => setShowRipple(true), 0);
      const t2 = setTimeout(() => setShowRipple(false), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [node._hidden]);

  const handleNodeClickInternal = (e) => {
    if (isStudyCard) {
      e.stopPropagation();
      onOpenStudyCard?.(node.id);
      return;
    }
    onNodeClick(e, node);
  };

  const handleNodeDoubleClick = (e) => {
    e.stopPropagation();
    if (!isFlashcardHidden && !isMobile && !isStudyCard) {
      onEditNode(node);
    }
  };

  const handleBlueMouseDown = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    e.stopPropagation();
    const now = Date.now();
    if (now - lastBlueClickRef.current < 300) {
      onAddNode(node);
      lastBlueClickRef.current = 0;
    } else {
      lastBlueClickRef.current = now;
      startConnect(node.id, node.x, node.y, node.type, e);
    }
  };

  const handlePointerDown = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    if (isStudyCard) {
      e.preventDefault();
      e.stopPropagation();
      onOpenStudyCard?.(node.id);
      return;
    }
    if (e.pointerType === 'touch') e.preventDefault();
    onMouseDown(e);
  };

  const handleMouseDown = (e) => {
    if (isStudyCard) {
      e.preventDefault();
      e.stopPropagation();
      onOpenStudyCard?.(node.id);
      return;
    }
    onMouseDown(e);
  };

  const rx = node.x - W / 2;
  const ry = node.y - H / 2;
  const dimmedOpacity = flashcardMode && !node.isFlashcard ? 0.22 : 1;
  const cardPalette = {
    new: { fill: '#fffdf7', stroke: '#e8c36a', accent: '#b9770e', shadow: 'rgba(232,195,106,0.22)' },
    revealed: { fill: '#f6fbff', stroke: '#82b1e3', accent: '#2b6cb0', shadow: 'rgba(130,177,227,0.2)' },
    review: { fill: '#fff7f2', stroke: '#ef9f76', accent: '#c05621', shadow: 'rgba(239,159,118,0.2)' },
    known: { fill: '#f7fff8', stroke: '#81c995', accent: '#2f855a', shadow: 'rgba(129,201,149,0.2)' },
  }[cardStatus];

  return (
    <g
      className="node-item-g"
      data-node-id={node.id}
      onMouseDown={!supportsPointer ? handleMouseDown : undefined}
      onPointerDown={supportsPointer ? handlePointerDown : undefined}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      onDoubleClick={handleNodeDoubleClick}
      style={{
        cursor: isStudyCard ? 'pointer' : (isFlashcardHidden ? 'pointer' : 'move'),
        opacity: node._hidden ? 0 : (isGhost ? 0.4 : dimmedOpacity),
        transform: node._hidden ? 'scale(0.75)' : 'scale(1)',
        transformOrigin: `${node.x}px ${node.y}px`,
        touchAction: 'none',
        transition: 'opacity 0.4s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      <style>{`
        @keyframes contextGlow {
          0% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
          100% { opacity: 0.3; transform: scale(1); }
        }
        @keyframes ghostPulse {
          0% { opacity: 0.2; }
          50% { opacity: 0.5; }
          100% { opacity: 0.2; }
        }
      `}</style>

      <rect
        x={rx - 20} y={ry - 20}
        width={W + 40} height={H + 40}
        fill="transparent"
        onClick={isStudyCard ? handleNodeClickInternal : undefined}
      />

      {isSelected && isAILoading && (
        <rect
          x={rx - 10} y={ry - 10}
          width={W + 20} height={H + 20}
          rx={NODE_RX + 10}
          fill={isStudyCard ? cardPalette.fill : fillColor}
          opacity="0.5"
          style={{ animation: 'contextGlow 2s ease-in-out infinite', pointerEvents: 'none' }}
        />
      )}

      {isGhost && (
        <rect
          x={rx} y={ry}
          width={W} height={H}
          rx={NODE_RX}
          fill={isStudyCard ? cardPalette.fill : fillColor}
          style={{ animation: 'ghostPulse 1.5s ease-in-out infinite', pointerEvents: 'none' }}
        />
      )}

      {showRipple && (
        <rect
          x={rx - 4} y={ry - 4}
          width={W + 8} height={H + 8}
          rx={NODE_RX + 4}
          fill="none"
          stroke={isStudyCard ? cardPalette.stroke : strokeColor}
          strokeWidth="3"
          style={{ animation: 'glowFade 1.2s ease-out forwards', pointerEvents: 'none' }}
        />
      )}

      {isMultiSelected && (
        <rect
          x={rx - 4} y={ry - 4}
          width={W + 8} height={H + 8}
          rx={NODE_RX + 4}
          fill="none"
          stroke="var(--ctp-blue)"
          strokeWidth="3"
          strokeDasharray="4,3"
          opacity="0.8"
        />
      )}

      {isSelected && (
        <rect
          x={rx - 4} y={ry - 4}
          width={W + 8} height={H + 8}
          rx={NODE_RX + 4}
          fill="none"
          stroke="var(--ctp-lavender)"
          strokeWidth="3"
          strokeDasharray="4,3"
          opacity="0.8"
        />
      )}

      <rect
        x={rx}
        y={ry}
        width={W}
        height={H}
        rx={NODE_RX}
        fill={isStudyCard ? cardPalette.fill : (isFlashcardHidden ? '#e2e8f0' : fillColor)}
        stroke={isStudyCard ? cardPalette.stroke : (isFlashcardHidden ? '#94a3b8' : outlineColor)}
        strokeWidth={isStudyCard ? 2 : (isFlashcardHidden ? 1.5 : outlineWidth)}
        opacity={node.banned ? 0.5 : 1}
        onClick={handleNodeClickInternal}
        style={{
          filter: isStudyCard
            ? `drop-shadow(0 10px 24px ${cardPalette.shadow})`
            : shadowColor !== 'transparent'
              ? `drop-shadow(0 8px 18px ${shadowColor})`
              : undefined,
        }}
      />

      {isStudyCard ? (
        <foreignObject x={rx + 10} y={ry + 10} width={W - 20} height={H - 20}>
          <div
            className="h-full w-full flex flex-col justify-between"
            onClick={handleNodeClickInternal}
            style={{ pointerEvents: 'none' }}
          >
            <div className="flex items-start justify-between gap-2">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[8px] font-bold uppercase tracking-[0.18em]"
                style={{ backgroundColor: `${cardPalette.accent}18`, color: cardPalette.accent }}
              >
                {cardType === 'cloze' ? 'Cloze' : cardType}
              </span>
              <span className="text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: cardPalette.accent, opacity: 0.8 }}>
                {cardStatus === 'known' ? 'Known' : cardStatus === 'review' ? 'Review' : 'New'}
              </span>
            </div>

            <div className="flex-1 flex flex-col justify-center min-h-0 py-1">
              <div
                className="text-[10px] leading-[1.3] font-medium text-[#4D6677]"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {maskTitleTerms(getHintText(cardFront, node.title), node.title)}
              </div>
            </div>

            <div className="pt-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#6C8796]">
              {t('editNode.flashcardOpen')}
            </div>
          </div>
        </foreignObject>
      ) : (
        <>
          <foreignObject
            x={rx + 10}
            y={ry}
            width={W - 20}
            height={H}
            style={{ pointerEvents: 'none' }}
            title={isFlashcardHidden ? node.summary : ''}
          >
            <div
              className="flex items-center justify-center h-full text-center px-1"
              style={{
                color: isFlashcardHidden ? '#64748b' : textColor,
                textDecoration: node.banned ? 'line-through' : 'none',
                fontSize: isFlashcardHidden ? '10px' : '11px',
                fontWeight: isFlashcardHidden ? '500' : '700',
                textTransform: isFlashcardHidden ? 'none' : 'uppercase',
                lineHeight: '1.2',
                letterSpacing: '0.02em',
                fontStyle: isFlashcardHidden ? 'italic' : 'normal',
              }}
            >
              {isFlashcardHidden ? getHintText(node.summary, node.title) : node.title}
            </div>
          </foreignObject>

          {showFlashcardIcons && node.isFlashcard && !node.marked && (
            <foreignObject x={node.x + W / 2 - 18} y={node.y - H / 2 - 22} width={36} height={36}>
              <div className="flex items-center justify-center" style={{ fontSize: '18px', lineHeight: 1, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }}>🎴</div>
            </foreignObject>
          )}
        </>
      )}

      {node.marked && (
        <foreignObject x={node.x + W / 2 - 18} y={node.y - H / 2 - 22} width={36} height={36}>
          <div className="flex items-center justify-center" style={{ fontSize: '24px', lineHeight: 1, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }}>💡</div>
        </foreignObject>
      )}

      {connectState.active && connectState.targetId === node.id && (
        <rect
          x={rx - 4}
          y={ry - 4}
          width={W + 8}
          height={H + 8}
          rx={NODE_RX + 4}
          fill="none"
          stroke="var(--ctp-blue)"
          strokeWidth="3"
          strokeDasharray="6,4"
          opacity="0.9"
        />
      )}

      {!isStudyCard && isHovered && !connectState.active && (
        <circle
          cx={node.x + W / 2}
          cy={node.y}
          r={7}
          fill="var(--ctp-blue)"
          stroke="var(--canvas-bg)"
          strokeWidth="2"
          style={{ cursor: 'crosshair' }}
          onMouseDown={!supportsPointer ? handleBlueMouseDown : undefined}
          onPointerDown={supportsPointer ? handleBlueMouseDown : undefined}
          onTouchStart={(e) => {
            if (supportsPointer) return;
            if (e.touches.length === 1) {
              e.stopPropagation();
              startConnect(node.id, node.x, node.y, node.type, {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY,
                stopPropagation: () => e.stopPropagation(),
                preventDefault: () => e.preventDefault()
              });
            }
          }}
        />
      )}
    </g>
  );
};
